import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTruck, 
  faSyncAlt, 
  faColumns, 
  faWindowRestore, 
  faRedo,
  faCompress,
  faExpand 
} from '@fortawesome/free-solid-svg-icons';
import SplitScreenContainer from '../../common/SplitScreenContainer';
import ReportChooser from '../ReportChooser';

// Хуки
import { useTrackMapCore } from './hooks/useTrackMapCore';
import { useMapUpdater } from './hooks/useMapUpdater';
import { useAutoLoad } from './hooks/useAutoLoad';
import { useSplitScreenManager } from './hooks/useSplitScreenManager';
import { useEventHandlers } from './hooks/useEventHandlers';
import { usePointSynchronization } from './hooks/usePointSynchronization';

// Стили TrackMap
import '../TrackMapStyles.css';

const getAuthToken = () => {
  return localStorage.getItem('access_token') || null;
};

const TrackReport = ({ 
  tabId, 
  vehicle, 
  startDate, 
  endDate
}) => {
  // Основные состояния
  const [currentVehicle, setCurrentVehicle] = useState(vehicle);
  const [currentDateRange, setCurrentDateRange] = useState({
    startDate: startDate ? new Date(startDate) : null,
    endDate: endDate ? new Date(endDate) : null
  });
  
  // ID контейнера
  const containerId = `track-report-container-${tabId || 'default'}`;

  // Функция проверки валидности даты
  const ensureValidDate = useCallback((dateInput) => {
    if (!dateInput) return null;
    
    let date;
    if (typeof dateInput === 'string') {
      date = new Date(dateInput);
    } else if (dateInput instanceof Date) {
      date = dateInput;
    } else {
      return null;
    }
    
    return isNaN(date.getTime()) ? null : date;
  }, []);

  // Функция форматирования даты
  const formatDate = useCallback((timestamp) => {
    if (!timestamp) return 'Н/Д';
    const date = new Date(timestamp);
    return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  }, []);

  // Функция форматирования времени
  const formatTime = useCallback((timestamp) => {
    if (!timestamp) return 'Н/Д';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('ru-RU');
  }, []);

  // Сохранение ТС в localStorage
  const saveVehicleToLocalStorage = useCallback((vehicle) => {
    try {
      localStorage.setItem('lastSelectedVehicle', JSON.stringify(vehicle));
    } catch (error) {
      console.error('TrackReport: Ошибка сохранения ТС в localStorage:', error);
    }
  }, []);

  // Сохранение диапазона дат в localStorage
  const saveDateRangeToLocalStorage = useCallback((range, timestamp) => {
    try {
      const dataToSave = {
        ...range,
        timestamp: timestamp || new Date().getTime()
      };
      localStorage.setItem('lastDateRange', JSON.stringify(dataToSave));
    } catch (error) {
      console.error('TrackReport: Ошибка сохранения дат в localStorage:', error);
    }
  }, []);

  // Основной хук для управления картой и данными
  const {
    isLoading,
    loadingProgress,
    loadingStatus,
    mapInitialized,
    trackData,
    mapContainerRef,
    mapInstanceRef,
    markersRef,
    routePathRef,
    animatedMarkerRef,
    highlightedPathRef,
    initializeMap,
    fetchTrackData
  } = useTrackMapCore(tabId, getAuthToken, currentVehicle, currentDateRange, setCurrentVehicle, formatDate);

  // Рефы для карты
  const mapRefs = { 
    mapInstanceRef, 
    markersRef, 
    routePathRef, 
    animatedMarkerRef, 
    highlightedPathRef,
    mapContainerRef
  };
  
  // Хук для синхронизации выбранных точек (как в TrackMap)
  const {
    highlightTrackPoint
  } = usePointSynchronization(
    mapRefs,
    trackData,
    currentVehicle,
    containerId,
    tabId,
    formatDate,
    formatTime
  );

  // Обработчик клика на карту (определяем после получения highlightTrackPoint)
  const handleMapClick = useCallback((e) => {
    try {
      console.log('TrackReport: Клик на карту:', e);
      
      if (!trackData || trackData.length === 0) {
        console.warn('TrackReport: Нет данных трека для обработки клика');
        return;
      }
      
      const { lat, lng } = e.latlng;
      
      let closestIndex = -1;
      let minDistance = Infinity;
      
      trackData.forEach((point, index) => {
        const pointLat = point.latitude;
        const pointLng = point.longitude;
        
        if (pointLat && pointLng) {
          const distance = Math.sqrt(
            Math.pow(pointLat - lat, 2) + 
            Math.pow(pointLng - lng, 2)
          );
          
          if (distance < minDistance) {
            minDistance = distance;
            closestIndex = index;
          }
        }
      });
      
      if (closestIndex >= 0) {
        console.log('TrackReport: Найдена ближайшая точка:', trackData[closestIndex], 'индекс:', closestIndex);
        // Используем highlightTrackPoint из хука синхронизации
        if (typeof highlightTrackPoint === 'function') {
          highlightTrackPoint(closestIndex);
        }
      } else {
        console.warn('TrackReport: Не удалось найти ближайшую точку к клику');
      }
    } catch (error) {
      console.error('TrackReport: Ошибка при обработке клика на карте:', error);
    }
  }, [trackData, highlightTrackPoint]);
  
  // Хук для обновления карты
  const {
    updateMapWithTrackData,
    refreshMapView: baseRefreshMapView
  } = useMapUpdater(mapRefs, formatDate, formatTime, handleMapClick);

  // Обертка для refreshMapView с обработкой реинициализации
  const refreshMapView = useCallback(() => {
    const success = baseRefreshMapView();
    if (!success && mapContainerRef.current && document.body.contains(mapContainerRef.current)) {
      console.log('TrackReport: Обновление размеров карты не удалось, реинициализируем');
      setTimeout(() => {
        initializeMap();
        setTimeout(() => {
          if (mapInstanceRef.current && trackData && trackData.length > 0) {
            updateMapWithTrackData(trackData, currentVehicle, initializeMap);
          }
        }, 300);
      }, 100);
    }
    return success;
  }, [baseRefreshMapView, mapContainerRef, initializeMap, mapInstanceRef, trackData, currentVehicle, updateMapWithTrackData]);

  // Хук для управления разделением экрана
  const {
    expandedMode,
    showReportChooser,
    toggleExpandedMode,
    // changeSplitMode,
    handleHorizontalSplit,
    handleVerticalSplit,
    handleSingleMode,
    handleGoBack,
    handleReportSelect,
    handleReportChooserClose
  } = useSplitScreenManager(refreshMapView, containerId, currentVehicle, currentDateRange);

  // Хук для обработки событий
  useEventHandlers(
    tabId,
    currentVehicle,
    setCurrentVehicle,
    currentDateRange,
    setCurrentDateRange,
    mapInitialized,
    fetchTrackData,
    ensureValidDate,
    saveVehicleToLocalStorage,
    saveDateRangeToLocalStorage,
    refreshMapView
  );

  // Хук для автоматической загрузки данных
  useAutoLoad({
    vehicle,
    startDate,
    endDate,
    currentVehicle,
    setCurrentVehicle,
    currentDateRange,
    setCurrentDateRange,
    mapInitialized,
    fetchTrackData,
    initializeMap,
    mapContainerRef,
    ensureValidDate,
    saveVehicleToLocalStorage,
    saveDateRangeToLocalStorage,
    formatDate,
    isLoading
  });

  // Обёртка для загрузки данных
  const handleFetchTrackData = useCallback(() => {
    if (currentVehicle && currentDateRange.startDate && currentDateRange.endDate) {
      fetchTrackData(currentVehicle, currentDateRange);
    }
  }, [currentVehicle, currentDateRange, fetchTrackData]);

  // Обновление карты при изменении данных трека
  useEffect(() => {
    if (trackData && trackData.length > 0) {
      console.log('TrackReport: Обновление карты с новыми данными трека');
      updateMapWithTrackData(trackData, currentVehicle, initializeMap);
    }
  }, [trackData, currentVehicle, updateMapWithTrackData, initializeMap]);

  // Главный метод рендеринга компонента
  return (
    <>
      <SplitScreenContainer
        id={containerId}
        data-container-id={containerId}
        className="split-screen-container"
        showControls={true}
        allowSplit={true}
        onSplitChange={(splitInfo) => {
          console.log('TrackReport: Получено событие разделения экрана', splitInfo);
          
          // Добавляем задержку для завершения DOM операций
          setTimeout(() => {
            // Проверяем существует ли контейнер карты в DOM
            const mapContainer = mapContainerRef.current;
            
            if (!mapContainer || !document.body.contains(mapContainer)) {
              console.log('TrackReport: Контейнер карты был удален, реинициализируем карту');
              
              // Очищаем ссылку на старый экземпляр карты
              if (mapInstanceRef.current) {
                try {
                  mapInstanceRef.current.remove();
                } catch (error) {
                  console.warn('TrackReport: Ошибка при удалении старого экземпляра карты:', error);
                }
                mapInstanceRef.current = null;
              }
              
              // Инициализируем карту заново
              initializeMap();
            } else if (!mapInstanceRef.current && mapContainer) {
              console.log('TrackReport: Контейнер карты существует, но карта не инициализирована, инициализируем');
              initializeMap();
            } else {
              console.log('TrackReport: После разделения обновляем размер карты');
              refreshMapView();
            }
          }, 300);
        }}
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <div 
          className={`tm-container ${expandedMode ? 'tm-expanded' : ''}`} 
          id={`inner-${containerId}`}
          data-component-type="trackReport"
          data-splitscreen="true"
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {isLoading && (
            <div className="tm-loading">
              <div className="tm-loading-spinner"></div>
              <div style={{ color: '#666', fontSize: '14px', textAlign: 'center' }}>
                <div>{loadingStatus || 'Загрузка данных...'}</div>
                {loadingProgress > 0 && (
                  <div style={{ marginTop: '5px', fontSize: '12px' }}>
                    {loadingProgress}%
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div className="tm-header">
            <div className="tm-title">
              <FontAwesomeIcon icon={faTruck} />
              <span>Трек: {currentVehicle?.name || 'Транспорт не выбран'}</span>
            </div>
            <div className="tm-controls">
              <div className="tm-control-group">
                <button 
                  className="tm-control-button tm-refresh-button"
                  onClick={handleFetchTrackData}
                  title="Обновить данные"
                >
                  <FontAwesomeIcon icon={faSyncAlt} />
                </button>
              </div>
              
              <div className="tm-control-group">
                <button
                  className="tm-control-button"
                  onClick={handleHorizontalSplit}
                  title="Разделить по горизонтали"
                >
                  <FontAwesomeIcon icon={faColumns} style={{ transform: 'rotate(90deg)' }} />
                </button>
                <button
                  className="tm-control-button"
                  onClick={handleVerticalSplit}
                  title="Разделить по вертикали"
                >
                  <FontAwesomeIcon icon={faColumns} />
                </button>
                <button
                  className="tm-control-button"
                  onClick={handleSingleMode}
                  title="Один экран"
                >
                  <FontAwesomeIcon icon={faWindowRestore} />
                </button>
                <button
                  className="tm-control-button"
                  onClick={handleGoBack}
                  title="Вернуться к предыдущему режиму"
                >
                  <FontAwesomeIcon icon={faRedo} style={{ transform: 'rotate(180deg)' }} />
                </button>
              </div>
              
              <div className="tm-control-group">
                <button 
                  className="tm-control-button"
                  onClick={toggleExpandedMode}
                  title={expandedMode ? "Свернуть" : "На весь экран"}
                >
                  <FontAwesomeIcon icon={expandedMode ? faCompress : faExpand} />
                </button>
              </div>
            </div>
          </div>
          
          <div className="tm-content" style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
            <div 
              className="tm-map-wrapper" 
              ref={mapContainerRef}
              style={{ 
                width: '100%', 
                height: '100%', 
                position: 'relative'
              }}
            >
              {/* Контейнер для карты */}
              <div 
                id={`map-${tabId || 'default'}`} 
                className="tm-map-instance"
                style={{ 
                  width: '100%', 
                  height: '100%',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  zIndex: 1
                }}
              ></div>
            </div>
          </div>
        </div>
      </SplitScreenContainer>
      
      {/* Модальное окно выбора отчета */}
      {showReportChooser && (
        <ReportChooser 
          onSelectReport={handleReportSelect}
          onClose={handleReportChooserClose}
          selectedVehicle={currentVehicle}
          originalReport="trackmap"
          containerId={containerId}
        />
      )}
    </>
  );
};

export default TrackReport; 